import { lazy, Suspense } from "react";
import { Switch, Route, Router, useLocation } from "wouter";
import { detectAppMode, engineHostSlug, engineBasePath } from "@/lib/app-routing";
import { detectEnv } from "@/lib/env";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AgeVerifiedRoute } from "@/lib/age-verified-route";
import { LanguageProvider } from "@/lib/language-context";
import NotFound from "@/pages/not-found";
import LandingPage from "@/pages/landing";

const Home = lazy(() => import("@/pages/home"));
const AgeGate = lazy(() => import("@/pages/age-gate"));
const AdminPage = lazy(() => import("@/pages/admin"));
const PlayPage = lazy(() => import("@/pages/play"));
const AnalyticsPage = lazy(() => import("@/pages/analytics"));
const RedeemPage = lazy(() => import("@/pages/redeem"));
const RegisterPage = lazy(() => import("@/pages/register"));
const PortalPage = lazy(() => import("@/pages/portal"));
const SuperAdminPage = lazy(() => import("@/pages/super-admin"));
const SchemaPage = lazy(() => import("@/pages/schema"));
const SignInPage = lazy(() => import("@/pages/signin"));
const PortalInvitePage = lazy(() => import("@/pages/portal-invite"));
const PrivacyPage = lazy(() => import("@/pages/privacy"));
const TermsPage = lazy(() => import("@/pages/terms"));
const PaymentSuccessPage = lazy(() => import("@/pages/payment-result").then(m => ({ default: m.PaymentSuccess })));
const PaymentCancelPage = lazy(() => import("@/pages/payment-result").then(m => ({ default: m.PaymentCancel })));
const ComplianceScanPage = lazy(() => import("@/pages/compliance"));
const ComplianceAdminPage = lazy(() => import("@/pages/compliance-admin"));
const ScanToWinAdminPage = lazy(() => import("@/pages/scan-to-win-admin"));
const SecurityPage = lazy(() => import("@/pages/security"));
const FieldScanPage = lazy(() => import("@/pages/field-scan"));
const ComplianceAnalyticsPage = lazy(() => import("@/pages/compliance-analytics"));
const OperatorPage = lazy(() => import("@/pages/operator"));
const ApproveAccountPage = lazy(() => import("@/pages/approve-account"));
const ClaimReceiptPage = lazy(() => import("@/pages/claim-receipt"));
const LoyaltyPage = lazy(() => import("@/pages/loyalty"));
const LoyaltyConfirmPage = lazy(() => import("@/pages/loyalty-confirm"));
const LoyaltyRedeemValidatePage = lazy(() => import("@/pages/loyalty-redeem-validate"));
const PrizeStatusPage = lazy(() => import("@/pages/prize-status"));
const EngineTestPage = lazy(() => import("@/pages/engine-test"));
const EnginePublicChatPage = lazy(() => import("@/pages/engine-chat"));
const TeamsHostPanel = lazy(() => import("@/components/engine/TeamsHostPanel"));
const EnginePortalPage = lazy(() => import("@/pages/engine-portal"));
const RetailAssistantPage = lazy(() => import("@/pages/retail-assistant"));
const EngineEmbedPage = lazy(() => import("@/pages/engine-embed"));
const SmartMailPage = lazy(() => import("@/pages/smartmail"));
const TeamChatPage = lazy(() => import("@/pages/team-chat"));
const DocsPage = lazy(() => import("@/pages/docs"));
const OpsPage = lazy(() => import("@/pages/ops"));
const DeliveryConfirmPage = lazy(() => import("@/pages/delivery-confirm"));
const CataloguePocPage = lazy(() => import("@/pages/catalogue-poc"));
const WalletPage = lazy(() => import("@/pages/wallet"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-400 text-sm">Loading...</p>
      </div>
    </div>
  );
}

function TalkPackRoutes() {
  return (
    <LanguageProvider>
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/" component={LandingPage} />
          <Route path="/signin" component={SignInPage} />
          <AgeVerifiedRoute path="/game" component={Home} />
          <Route path="/verify" component={AgeGate} />
          <Route path="/admin" component={AdminPage} />
          <Route path="/admin/super" component={SuperAdminPage} />
          <Route path="/admin/analytics/:id" component={AnalyticsPage} />
          <Route path="/play/:id" component={PlayPage} />
          <Route path="/redeem/:id" component={RedeemPage} />
          <Route path="/prize/:id" component={PrizeStatusPage} />
          <Route path="/wallet/:token" component={WalletPage} />
          <Route path="/scan/:fixtureId" component={ComplianceScanPage} />
          <Route path="/field-scan/:campaignId" component={FieldScanPage} />
          <Route path="/compliance/:campaignId" component={ComplianceAdminPage} />
          <Route path="/stw-admin/:campaignId" component={ScanToWinAdminPage} />
          <Route path="/compliance-analytics/:campaignId" component={ComplianceAnalyticsPage} />
          <Route path="/register" component={RegisterPage} />
          <Route path="/register/success" component={PaymentSuccessPage} />
          <Route path="/register/cancel" component={PaymentCancelPage} />
          <Route path="/portal/:slug" component={PortalPage} />
          <Route path="/portal-invite" component={PortalInvitePage} />
          <Route path="/privacy/:id" component={PrivacyPage} />
          <Route path="/terms/:id" component={TermsPage} />
          <Route path="/security" component={SecurityPage} />
          <Route path="/operator/:token" component={OperatorPage} />
          <Route path="/admin/approve" component={ApproveAccountPage} />
          <Route path="/loyalty/:storeSlug" component={LoyaltyPage} />
          <Route path="/loyalty-confirm/:storeSlug/:token" component={LoyaltyConfirmPage} />
          <Route path="/loyalty-redeem/:storeSlug/:token" component={LoyaltyRedeemValidatePage} />
          <Route path="/schema" component={SchemaPage} />
          <Route path="/delivery/:token" component={DeliveryConfirmPage} />
          <Route path="/claim/:token" component={ClaimReceiptPage} />
          <Route path="/c/:campaignId" component={CataloguePocPage} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </LanguageProvider>
  );
}

function TalkPackApp({ base = "" }: { base?: string }) {
  if (base) {
    return (
      <Router base={base}>
        <TalkPackRoutes />
      </Router>
    );
  }
  return <TalkPackRoutes />;
}

function EngineApp() {
  return (
    <Suspense fallback={<PageLoader />}>
      <EngineRoutes />
    </Suspense>
  );
}

function SmartMailApp() {
  return (
    <Suspense fallback={<PageLoader />}>
      <SmartMailPage />
    </Suspense>
  );
}

function EngineRoutes() {
  const [location] = useLocation();

  if (location === engineBasePath() + "/teams-app") return <TeamsHostPanel />;

  // Per-tenant subdomain (e.g. dash.dev.shopai.website): the slug comes from the
  // host, so the path carries only the view/module — there is NO leading slug
  // segment. "/" → chat hub, "/v/<view>" → built-in view, "/<module>" → portal.
  const hostSlug = engineHostSlug();
  if (hostSlug) {
    const m = location.match(/^\/(?:v\/([^/]+)|([^/]+))?/);
    const view = m?.[1] || "";
    const portalModule = m?.[2] || "";
    if (portalModule) {
      return <EnginePortalPage slug={hostSlug} moduleName={portalModule} />;
    }
    if (view === "shop") {
      return <RetailAssistantPage slug={hostSlug} />;
    }
    if (view === "embed") {
      // Third-party iframe widget (loader.js) — identity arrives via
      // postMessage, not from any first-party token.
      return <EngineEmbedPage />;
    }
    return <EnginePublicChatPage slug={hostSlug} view={view} />;
  }

  // Path-prefixed spaces (apex/localhost → "/engine"; engine.* app subdomain → ""):
  //   ADMIN:  <prefix>, <prefix>/v/<tab>
  //   TENANT: <prefix>/<slug>(/v/<view>|/<portalModule>)?
  // The /v/ prefix disambiguates built-in views from portal module names; using
  // "v" as the leading segment routes to admin (no real tenant slug is "v").
  const prefix = engineBasePath();

  // Admin: bare prefix or /v/<tab>
  if (location === prefix || location === prefix + "/") {
    return <EngineTestPage view="" />;
  }
  const adminMatch = location.match(new RegExp(`^${prefix}/v/([^/]+)`));
  if (adminMatch) {
    if (adminMatch[1] === "inbox") {
      return <SmartMailPage />;
    }
    if (adminMatch[1] === "teamchat") {
      // Internal team room (Chris, Ilann + Claude sessions) — standalone
      // page with its own login gate, admin-side only (no tenant variant).
      // Dev-only surface: prod hosts don't render it (and the prod backend
      // unmounts its API under TEAMCHAT_DISABLE — see internal/router).
      if (detectEnv() === "prod") return <NotFound />;
      return <TeamChatPage />;
    }
    if (adminMatch[1] === "docs") {
      // Docs reader: dev-only like the room it threads comments into; prod
      // also unmounts its API under TEAMCHAT_DISABLE.
      if (detectEnv() === "prod") return <NotFound />;
      return <DocsPage />;
    }
    if (adminMatch[1] === "ops") {
      // Ops dashboard (usage/latency/cost) — same standalone-page +
      // login-gate pattern as the team room.
      return <OpsPage />;
    }
    return <EngineTestPage view={adminMatch[1]} />;
  }

  // Tenant: /(prefix/)<slug>(/v/<view>|/<portalModule>)?
  const tenantRe = new RegExp(`^${prefix}/([^/]+)(?:/(?:v/([^/]+)|([^/]+)))?`);
  const m = location.match(tenantRe);
  const slug = m?.[1] || "";
  const view = m?.[2] || "";
  const portalModule = m?.[3] || "";

  if (slug && portalModule) {
    return <EnginePortalPage slug={slug} moduleName={portalModule} />;
  }
  if (slug && view === "shop") {
    return <RetailAssistantPage slug={slug} />;
  }
  if (slug && view === "embed") {
    return <EngineEmbedPage />;
  }
  if (slug) {
    return <EnginePublicChatPage slug={slug} view={view} />;
  }
  return <EngineTestPage view="" />;
}

function RootLanding() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950">
      <div className="flex flex-col items-center gap-8">
        <h1 className="text-3xl font-bold text-white">ShopAI Platform</h1>
        <div className="flex gap-4">
          <a
            href="/talkpack/"
            className="px-6 py-3 bg-amber-500 text-gray-950 font-semibold rounded-lg hover:bg-amber-400 transition-colors"
          >
            TalkPack
          </a>
          <a
            href="/engine"
            className="px-6 py-3 bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-600 transition-colors"
          >
            Engine
          </a>
          <a
            href="/smartmail"
            className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-500 transition-colors"
          >
            SmartMail
          </a>
          {detectEnv() !== "prod" && (
            // Dev-only surface — the chooser also renders on the prod apex,
            // where this link would 404 (TEAMCHAT_DISABLE).
            <a
              href="/engine/v/teamchat"
              className="px-6 py-3 bg-violet-600 text-white font-semibold rounded-lg hover:bg-violet-500 transition-colors"
            >
              Team Chat
            </a>
          )}
          {detectEnv() !== "prod" && (
            // Dev-only like Team Chat: the prod apex has no docs reader.
            <a
              href="/engine/v/docs"
              className="px-6 py-3 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-500 transition-colors"
            >
              Documents
            </a>
          )}
          <a
            href="/engine/v/ops"
            className="px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-500 transition-colors"
          >
            Ops
          </a>
        </div>
      </div>
    </div>
  );
}

/**
 * Apex/localhost: app prefixes (/engine, /smartmail, /talkpack), the platform
 * chooser at exactly "/", and TalkPack consumer routes for every other path.
 */
function LocalRouter() {
  const [location] = useLocation();
  if (location.startsWith("/engine")) return <EngineApp />;
  if (location.startsWith("/smartmail")) return <SmartMailApp />;
  if (location.startsWith("/talkpack")) return <TalkPackApp base="/talkpack" />;
  if (location === "/") return <RootLanding />;
  // Any other path is a TalkPack consumer route, served at the origin root
  // exactly as on the talkpack.* subdomain. The backend mints absolute links
  // as APP_URL + "/play/<slug>" (portal QR/share dialog, GS1 resolver, social
  // links, redemption emails) and on dev APP_URL is the apex host — those
  // links, the /c/:campaignId catalogue links, and any in-app navigation from
  // such an entry point (/terms, /redeem, ...) must render rather than fall
  // through to the platform chooser, which now only owns the exact root.
  return <TalkPackApp base="" />;
}

function App() {
  const appMode = detectAppMode();

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        {appMode === "engine" && <EngineApp />}
        {appMode === "talkpack" && <TalkPackApp />}
        {appMode === "smartmail" && <SmartMailApp />}
        {appMode === "local" && <LocalRouter />}
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
